import type { ControlInfo, ReadOnlyRibbonControlDefProps } from "owa-mail-ribbon-utils";
import { type MailRibbonControlId, type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import { createMailAppButton, createMailAppFlyoutAnchor, createMailAppSplitButton, } from "owa-mail-ribbon-utils/lib/createMailRibbonButton";
import { onNewEmailSplitButtonExecuted, onNewMessage, onNewNoteClicked, onNewPost, } from "owa-mail-ribbon-command-actions";
import { type MenuItemType } from "owa-filterable-menu/lib/components/MenuItemType";
import type { AppButtonOnExecuteParameter, MenuOpenOnExecuteParameter, RibbonControlProps, } from "owa-acui";
import { owaComputedFn } from "owa-computed-fn";
import { constructKeytip } from "owa-command-ribbon";
import { getControlInfo } from "owa-mail-ribbon-utils/lib/getControlInfo";
import { getMailMenuItemShouldShow } from "owa-mail-filterable-menu-behavior";
import { getMenuDefinition } from "../../../store/menuDefinitionStore";
import { getRibbonMailboxInfo } from "owa-mail-ribbon-utils/lib/getRibbonMailboxInfo";
import { isPublicFolderSelected } from "owa-publicfolder-utils";
import { onNewGroupMenuClicked } from "../../utils/deferredOnNewGroupMenuClicked";
import { resetMenuDefinitionMutator } from "../../../mutatorActions/menuDefinitionMutatorActions";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";

/**
 * This file contains the definitions for all the controls
 * in the Multi Line Ribbon (MLR)
 */

const newGroupMenuStoreName = "NewGroupMenuDefinitionStore";

export const getNewMessageControl: (keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getNewMessageControl(keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const isNote: boolean = getMailMenuItemShouldShow(57, mailboxInfo);
    const isPublicFolder: boolean = isPublicFolderSelected();
    const controlInfo: ControlInfo = getControlInfo(588, props);
    const isNewMessageDisabled = !getMailMenuItemShouldShow(56, mailboxInfo) && !isNote;
    
    if (isWin32StylingEnabled()) {
        const onNewMessageOrPost = (parameter?: AppButtonOnExecuteParameter) => (isPublicFolder ? onNewPost : onNewMessage)(parameter?.event?.timeStamp);
        
        return createMailAppButton(588, controlInfo.text, controlInfo.tooltip, props, isNote ? onNewNoteClicked : onNewMessageOrPost, constructKeytip([keytipTabId], 588), controlInfo.iconName, undefined /*iconColor*/, undefined /*styles*/, undefined /*itemId*/, {
            disabled: isNewMessageDisabled,
        });
    }
    
    return createMailAppSplitButton(588, controlInfo.text, controlInfo.ariaText, controlInfo.tooltip, props, getMenuDefinition(newGroupMenuStoreName), isNote
        ? onNewNoteClicked
        : param => {
            isPublicFolder
                ? onNewPost(param?.event?.timeStamp)
                : onNewMessage(param?.event?.timeStamp);
        }, constructKeytip([keytipTabId], 588, true /* hasMenu */), controlInfo.iconName, undefined /*iconColor*/, undefined /*styles*/, undefined /*itemId*/, {
        onMenuButtonExecute: (parameter: MenuOpenOnExecuteParameter) => {
            onNewEmailSplitButtonExecuted(parameter, mailboxInfo);
            onNewGroupMenuClicked(keytipTabId, props);
        },
        onAfterMenuDismiss: () => resetMenuDefinitionMutator(newGroupMenuStoreName),
        primaryDisabled: isNewMessageDisabled,
    });
});

export const getNewItemsControl: (keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getNewItemsControl(keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(586, props);
    
    return createMailAppFlyoutAnchor(586, controlInfo.text, controlInfo.tooltip, props, getMenuDefinition(newGroupMenuStoreName), mailboxInfo, constructKeytip([keytipTabId], 586, true /* hasMenu */), controlInfo.iconName, undefined /*iconColor*/, undefined /*styles*/, undefined /*itemId*/, {
        onExecute: (parameter: MenuOpenOnExecuteParameter) => {
            onNewEmailSplitButtonExecuted(parameter, mailboxInfo);
            onNewGroupMenuClicked(keytipTabId, props, 586);
        },
        onAfterMenuDismiss: () => resetMenuDefinitionMutator(newGroupMenuStoreName),
    });
});
